import React, { useEffect, useMemo, useState } from 'react';
import { FormSection, PageContainer, PageHeader } from "../../../components";
import { Button } from "@mui/material";
import { Alert, CircularProgress, Grid, Typography } from "@mui/material";
import { RESOLUTION, RESOLUTION_EDIT_ROUTE } from "../../../routes";
import { useHistory, useParams } from "react-router-dom";
import DashboardLayout from "../../../layouts/Dashboard";
import { FormProvider } from "../../../components/hook-form";
import RHFTextField from "../../../components/hook-form/RHFTextField";
import useReload from "../../../hooks/useReload";
import { useForm, useWatch } from "react-hook-form";
import LoadingButton from "@mui/lab/LoadingButton";
import RHFActField from "../../../components/hook-form/RHFActField";
import RHFDate from "../../../components/hook-form/RHFDate";
import AnObjectField from "../../../components/Relations/AnObjectField";
import FactorObjectFields from "../../../components/Relations/FactorObjectFields";
import { FilialDivisionSelect2, FilialSelect, PurposeOfSelectionsSelectFiltered } from "../../../components/Select/domainSelects";
import EmployeeField from "../../../components/Relations/DoctorField";
import { yupResolver } from "@hookform/resolvers/yup";
import { resolutionSchema } from "../../../models/schemas/resolutionSchema";
import { actCRUD, entityCRUD, factorCRUD, planCRUD, protocolCRUD, resolutionActCRUD, resolutionCRUD } from "../../../http";
import useLoader from "../../../hooks/useLoader";
import RHFIndicator from "../../../components/hook-form/RHFIndicator";
import { REQUEST_STATE_ID_CREATED, REQUEST_STATE_ID_DECLINED } from "../../../redux/constants/requestStateConstants";
import RHFGovernment from "../../../components/hook-form/RHFGovernment";
import { asJSONSafe, renderControls, RequestStateChecker, State } from "../../../functions";
import { controlsKSEK } from "../../../sections/Header/KSESHeader";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { ORGANIZATION_ID_NCE } from '../../../redux/constants/organizationConstants';
import useRoles from '../../../hooks/useRoles';
import ResultsAndProtocolsSection from "../../../sections/ResultsAndProtocolsSection";
import EditResultAndProtocolSection from "../../../sections/EditResultAndProtocolSection";
import ProtocolDetails from "../../Protocol/Popups/ProtocolDetails";
import { addRange, distinct } from '../../../functions/linqSimulation';
import { normalizeIndicators } from '../../../functions/normalization';
import { buildResolutionIndicatorsFromPlanEntity } from '../../../functions/planIndicatorsSerialization';
import ResolutionPdf from './ResolutionPdf';
import useDialogs from '../../../components/Dialog/useDialogs';
import { requestStateIds } from '../../../models/requestStateIds';
import { formatDate } from '../../../functions/formatters';
import { filterSignedProtocols } from '../../../models/protocolStateIds';

/** Алиас для читаемости JSX; не использовать OrderTypeSelect (тип заказа заменён на цель отбора). */
const PurposeOfSelectionSelect = PurposeOfSelectionsSelectFiltered;

function ResolutionCreate() {
    const { push } = useHistory();
    const authorizedEmployee = useSelector(state => state.employee.authorizedEmployee)
    const defaultValues = useMemo(() => ({
        targetDate: new Date(),
        createdAt: new Date(),
        number: '',
        acts: [],
        indicators: [],
        targetObjectId: '',
        targetObject: {},
        purposeId: null,
        purpose: null,
        factorId: null,
        factor: null,
        objectId: null,
        committeeObject: null,
        standards: [],
        createdBy: {},
        createdById: "",
        filialId: "",
        filialDivisionId: "",
    }), []);
    const { id } = useParams();
    const { reload, reloadValue } = useReload();
    const isEditing = useMemo(() => id, [id]);
    const [isCreated, setIsCreated] = useState(true);
    const [recreated, setRecreated] = useState(false);
    const { employee, isInRoles, isAdministrator } = useRoles();
    const [res, setRes] = useState({});
    const [pdfOpen, setPdfOpen] = useState(false);

    const [protocols, setProtocols] = useState([]);

    const disabled = (!isCreated && isEditing)

    const methods = useForm({
        defaultValues,
        resolver: yupResolver(resolutionSchema)
    });

    const {
        loading: fetchLoading,
        start: fetchStart,
        stop: fetchStop,
    } = useLoader(false);

    const fetchProtocols = async (acts, resolutionId) => {
        const result = [];

        acts = distinct(acts, (a, b) => a.actId == b.actId);

        for (let i = 0; i < acts.length; i++) {
            const item = acts[i];
            const actId = item.actId;

            if (!actId) {
                continue;
            }

            const protocolsResponse = await protocolCRUD.search({
                filter: {
                    actId: {
                        operand1: actId,
                        operator: 'equals'
                    }
                },
                paging: { skip: 0, take: 250 }
            });
            const protocols2 = filterSignedProtocols(protocolsResponse.result);

            addRange(result, protocols2);
        }

        setProtocols(result);
    }


    useEffect(() => {
        (async function () {
            if (isEditing) {
                fetchStart()
                const res = await resolutionCRUD.get(id);
                setIsCreated(res?.state?.code === REQUEST_STATE_ID_CREATED)
                const acts = res?.acts.map(el => ({
                    ...el?.act,
                    id: el.id,
                    actId: el.actId,
                    referralId: el.referralId,
                    resolutionId: el.resolutionId ?? res.id,
                }))
                setRes(res)
                setRecreated(res.recreated);
                fetchProtocols(res.acts, id);
                const indicators = normalizeIndicators(asJSONSafe(res?.indicators));
                const { purposeOfSelectionId: _psId, purposeOfSelection: _ps, ...resRest } = res;
                let purposeResolved = res.purpose ?? null;
                const purposeIdResolved = res.purposeId ?? res.purpose?.id ?? res.purposeOfSelectionId ?? null;
                if (purposeIdResolved && !purposeResolved?.name) {
                    try {
                        purposeResolved = await entityCRUD("purposeOfSelection").get(purposeIdResolved);
                    } catch {
                        purposeResolved = res.purpose ?? null;
                    }
                }
                let factorResolved = res.factor ?? null;
                const factorIdResolved = res.factorId ?? res.factor?.id ?? null;
                if (factorIdResolved && !factorResolved?.name) {
                    try {
                        factorResolved = await factorCRUD.get(factorIdResolved);
                    } catch {
                        factorResolved = res.factor ?? null;
                    }
                }
                reset({
                    ...resRest,
                    acts,
                    purposeId: purposeIdResolved,
                    purpose: purposeResolved,
                    factorId: factorIdResolved,
                    factor: factorResolved,
                    objectId: res.objectId ?? res.object?.id ?? res.committeeObject?.id ?? null,
                    committeeObject: res.object ?? res.committeeObject ?? null,
                    indicators: indicators,
                    standards: asJSONSafe(res?.standards),
                    createdBy: authorizedEmployee,
                    createdById: authorizedEmployee.id
                });
                fetchStop()
            }
        }())
    }, [reloadValue]);

    const { t } = useTranslation()

    const {
        handleSubmit,
        setValue,
        control,
        reset,
        watch,
        formState: { isSubmitting },
    } = methods;

    const targetObject = useWatch({
        control,
        name: "targetObject",
    });

    const purposeId = useWatch({
        control,
        name: "purposeId",
    });
    const purpose = useWatch({
        control,
        name: "purpose",
    });
    const factorId = useWatch({
        control,
        name: "factorId",
    });
    const factor = useWatch({
        control,
        name: "factor",
    });
    const objectId = useWatch({
        control,
        name: "objectId",
    });

    const createdBy = useWatch({
        control,
        name: "createdBy",
    });

    const filialId = useWatch({
        control,
        name: "filialId",
    });
    const filialDivisionId = useWatch({
        control,
        name: "filialDivisionId",
    });
    const indicators = useWatch({
        control,
        name: "indicators",
        defaultValue: []
    });
    const trace = useWatch({
            control,
            name: "trace",
            defaultValue: []
        });
    const processingTrace = trace.find(_ => _.stateId == requestStateIds.processing);

    const value = watch()

    const state = useMemo(
        () => new RequestStateChecker(value?.state?.code),
        [value?.state?.code]
    );
    const stateCode = state?.stateCode?.toLowerCase();

    const onSubmit = async state => {
        if (!filialId) {
            alert('Пожалуйста, выберите филиал');
            return;
        }

        for (let i = 0; i < indicators.length; i++) {
            const indicator = indicators[i];
            if (!indicator?.departmentId) {
                alert('У показателей не указана лаборатория');
                return;
            }
        }

        if (isEditing) {
            const data = { ...state }
            const acts = data?.acts?.map(el => ({
                id: el.id ?? 0,
                actId: el.actId,
                referralId: el.referralId,
                resolutionId: el.resolutionId ?? +id,
            }))
            delete data.orderTypeId;
            delete data.orderType;
            delete data.purposeOfSelectionId;
            delete data.purposeOfSelection;
            delete data.purpose;
            delete data.factor;
            delete data.committeeObject;
            delete data.object;
            delete data.targetObjectLegalPersonType;
            delete data.targetObjectLegalPersonTypeId;
            delete data.targetObjectSection;
            delete data.targetObjectSectionId;
            delete data.targetObjectSubSection;
            delete data.targetObjectSubSectionId;
            await resolutionCRUD.edit({
                ...data,
                id: +id,
                acts: [...acts],
                indicators: JSON.stringify(state.indicators),
                standards: JSON.stringify(state.standards),
            })
        } else {
            console.log('Creating resolution with data:', state);
            try {
                const {
                    orderTypeId: _o1,
                    orderType: _o2,
                    purposeOfSelectionId: _p1,
                    purposeOfSelection: _p2,
                    purpose: _purpose,
                    factor: _factor,
                    committeeObject: _co,
                    object: _obj,
                    ...createPayload
                } = state;
                const response = await resolutionCRUD.create({
                    ...createPayload,
                    indicators: JSON.stringify(state.indicators),
                    standards: JSON.stringify(state?.standards),
                });
                console.log('Resolution created successfully:', response);
                console.log('Redirecting to:', RESOLUTION_EDIT_ROUTE(response.id));
                return push(RESOLUTION_EDIT_ROUTE(response.id));
            } catch (error) {
                console.error('Error creating resolution:', error);
                alert('Ошибка при создании постановления: ' + error);
            }
        }
    }

    React.useEffect(() => {
        if (authorizedEmployee) {
            setValue('createdBy', authorizedEmployee)
            setValue('createdById', authorizedEmployee.id)
        }
    }, [authorizedEmployee, authorizedEmployee?.id])


    useEffect(() => {
        const acts = value?.acts;

        if (!acts || acts.length == 0) {
            return;
        }

        let data = acts[0].data;

        if (!data) {
            return;
        }

        data = JSON.parse(data);

        let obj = data?.object?.value;

        if (obj?.length)
            obj = obj[0];

        // if (!obj?.id) {
        //     alert('Невозможно прикрепить акт, так как у данного акта отсутствует поле Объект')
        //     return;
        // }

        if (obj?.id) {
            setValue('targetObjectId', obj.id);
            setValue('targetObject', obj);
        }
    }, [value?.acts])

    const {
        start: startSubmit,
        stop: stopSubmit,
    } = useLoader(false);

    const [editProtocolOpen, setEditProtocolOpen] = useState(false);
    const [editProtocol, setEditProtocol] = useState(null);

    const handleEditProtocolOpen = async (protocolId) => {
        await startSubmit();
        const protocol = await protocolCRUD.get(protocolId);
        await setEditProtocol(protocol);
        await setEditProtocolOpen(true);

        await stopSubmit();
    }

    const {alert, DIALOG} = useDialogs();

    useEffect(() => {
        if (isEditing) return;
        const bin =
            targetObject?.bin != null ? String(targetObject.bin).trim() : "";
        if (!bin || !targetObject?.id) return;

        let cancelled = false;
        (async () => {
            try {
                const searchRes = await planCRUD.search({
                    paging: { skip: 0, take: 25, returnCount: true },
                    filter: {
                        individualOrBusinessId: {
                            operator: "equals",
                            operand1: bin,
                        },
                    },
                    sort: {
                        id: { operator: "desc", ordinal: 0 },
                    },
                });
                const plans = searchRes?.result || [];
                if (cancelled || !plans.length) return;

                const planId = plans[0]?.id ?? plans[0]?.Id;
                if (planId == null) return;
                const planEntity = await planCRUD.get(planId);
                if (cancelled) return;

                const groups = buildResolutionIndicatorsFromPlanEntity(planEntity);
                if (groups?.length) {
                    setValue("indicators", groups, {
                        shouldDirty: true,
                        shouldValidate: true,
                    });
                }
            } catch {
                /* план по ИИН/БИН необязателен */
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [isEditing, targetObject?.id, targetObject?.bin, setValue]);

//
    return (
        <DashboardLayout>
            <DIALOG/>
            <PageHeader controls={renderControls(controlsKSEK(value, reload, push, resolutionCRUD, RESOLUTION, null, 'Resolution', isInRoles, isAdministrator, { protocols }), {
                state: stateCode,
                roles: [],
            })} heading={isEditing ? `Постановление #${value?.number || ""} ${stateCode === State.Processsing ? '(отправлено в НЦЭ)' :
                stateCode ? `(${t(`dashboard.state.${stateCode}`)})` : ""
                }` : "Постановление: Создание"}
                additionalControls={[
                    <Button
                        variant={"text"}
                        onClick={() => setPdfOpen(true)}
                    >
                        Просмотр Pdf
                    </Button>
                ]}>            
            </PageHeader>
            {fetchLoading ? (
                <Grid item xs={12}>
                    <CircularProgress />
                </Grid>
            ) : <PageContainer>
                <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
                    {isEditing && stateCode === State.Created && (
                        <Alert severity="warning" variant="outlined" sx={{ mb: 2 }}>
                            Пожалуйста, проверьте все показатели, прежде чем отправить в НЦЭ. В случае
                            неправильного постановления, оно будет отклонено.
                        </Alert>
                    )}
                    {isEditing && stateCode === State.Processsing && (
                        <Alert severity="warning" variant="outlined" sx={{ mb: 2 }}>
                            Пожалуйста, проверьте все показатели, прежде чем начать исследования. Если протокол по
                            постановлению будет подписан, отклонить его будет уже невозможно.
                        </Alert>
                    )}
                    {isEditing && <Grid container>                        
                        <Grid item xs={12}>
                            <div>Дата создания: {formatDate(value.createdAt)}</div>
                            {processingTrace && <div>Дата отправления в НЦЭ: {formatDate(processingTrace.changedAt)}</div>}
                        </Grid>
                    </Grid>}
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "flex-end",
                            paddingBottom: "20px",
                        }}
                    >
                        <p>
                            Приложение 3 к правилам проведения санитарно- эпидемиологической
                            экспертизы
                        </p>
                    </div>
                    <div style={{ display: "flex", justifyContent: "flex-end" }}>
                        <p>Форма</p>
                    </div>
                    <h4 style={{ textAlign: "center", fontWeight: '700' }}>
                        Постановление о проведении санитарно-эпидемиологической экспертизы
                    </h4>
                    {protocols?.length ? <FormSection heading="Протоколы" mb={4}>
                        <ResultsAndProtocolsSection
                            resolution
                            onProtocolClick={async (_) => {
                                await handleEditProtocolOpen(_);
                            }}
                            protocols={protocols}
                            resultsReloadValue={reloadValue}
                        />
                    </FormSection> : null}
                    {employee ?
                        <Grid container>
                            <FilialSelect
                                style={{ marginBottom: "5px" }}
                                label={"Филиал пользователя"}
                                fullWidth
                                disabled={true}
                                value={employee.filialId}
                            />
                            {employee.filialDivisionId && <FilialDivisionSelect2
                                style={{ marginBottom: "5px" }}
                                label={"Районное отделение пользователя"}
                                fullWidth
                                disabled={true}
                                value={employee.filialDivisionId}
                                filialId={employee.filialId === 1 ? null : employee.filialId}
                            />}
                        </Grid>
                        : null}
                    <Grid container spacing={1}>
                        <Grid item xs={12}>
                            {stateCode == 'declined' && <>
                                <label>Комментарий: {value.comment}</label>
                            </>}
                        </Grid>
                        <Grid item xs={6}>
                            <RHFTextField disabled={true} label="№" name="number" />
                        </Grid>
                        <Grid item xs={6}>
                            <RHFDate disabled={disabled} label="Дата" name="createdAt" />
                        </Grid>
                        <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center' }}>
                            <p style={{ width: "50%" }}>Дата назначения</p>
                            <RHFDate disabled={disabled} style={{ width: "50%", marginLeft: "30px" }}
                                label="Дата назначения"
                                name="targetDate" />
                        </Grid>
                        <Grid item xs={12} sx={{ marginBottom: '10px' }}>
                            {!isEditing && (
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                    Если для объекта есть план с тем же ИИН/БИН, в блок «Показатели»
                                    подставятся лаборатория, группа и показатели из последнего такого плана.
                                </Typography>
                            )}
                            <AnObjectField disabled={disabled}
                                label="Наименование проверяемого субъекта"
                                value={targetObject}
                                onChange={(obj) => {
                                    setValue('targetObject', obj)
                                    setValue('targetObjectId', obj.id)
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center' }}>
                            <p style={{ width: "50%" }}>
                                Должностное лицо санитарно-эпидемиологической службы
                            </p>
                            <RHFTextField disabled={disabled} style={{ width: "50%", marginLeft: "30px" }} label="ФИО"
                                name="responsibleFullName" />
                        </Grid>
                        <Grid item xs={12}>
                            <Alert severity="info" variant="outlined" sx={{ mb: 1 }}>
                                К постановлению можно прикрепить акты госзаказа и направления.
                            </Alert>
                            <RHFActField
                                disabled={disabled}
                                governmentActsOnly
                                label="Рассмотрев представленные документы"
                                reload={reload}
                            >
                                Рассмотрев представленные документы
                            </RHFActField>
                        </Grid>
                        <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center' }}>
                            <p style={{ width: "50%" }}>Установил (указать характер складывающейся
                                санитарно-эпидемиологической ситуации на
                                территории, группе объектов, нарушения законодательства Республики Казахстан в области
                                санитарно-эпидемиологического благополучия населения)
                            </p>
                            <RHFTextField disabled={disabled} style={{ width: "50%", marginLeft: "30px" }}
                                label="Характер ситуации"
                                name="establishedSituationDescription" />
                        </Grid>


                        <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center' }}>
                            <p style={{ width: "50%" }}>
                                Основание вынесения Постановления
                            </p>
                            <PurposeOfSelectionSelect
                                disabled={disabled}
                                fullWidth
                                formStyle={{ width: "50%", marginLeft: "30px" }}
                                required={false}
                                value={purposeId}
                                onChange={(val, opt) => {
                                    setValue('purposeId', val);
                                    setValue('purpose', opt || null);
                                    setValue('factorId', null);
                                    setValue('factor', null);
                                    setValue('objectId', null);
                                    setValue('committeeObject', null);
                                }}
                            />
                        </Grid>
                        <FactorObjectFields
                            disabled={disabled}
                            purpose={purpose}
                            factor={factor}
                            factorId={factorId}
                            objectId={objectId}
                            setValue={setValue}
                        />
                        <Grid item xs={12}>
                            <EmployeeField
                                disabled
                                label={"Создал заявку"}
                                fullWidth
                                object={createdBy}
                                value={createdBy?.id}
                                onChange={(obj) => {
                                    setValue('createdBy', obj)
                                    setValue('createdById', obj.id)
                                }}
                            />
                        </Grid>
                        <h6 style={{ fontWeight: "bold" }}>Постановил</h6>
                        <Grid item xs={12}>
                            <FilialSelect
                                disabled={disabled}
                                label={"Филиал"}
                                fullWidth
                                organizationId={ORGANIZATION_ID_NCE}
                                value={filialId}
                                onChange={(val) => {
                                    setValue('filialId', val)
                                    setValue('filialDivisionId', Number(val) !== Number(filialId)
                                        ? null
                                        : filialDivisionId)
                                }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FilialDivisionSelect2
                                disabled={disabled}
                                label={"Наименование отделения"}
                                fullWidth
                                organizationId={ORGANIZATION_ID_NCE}
                                value={filialDivisionId}
                                filialId={filialId === 1 ? null : filialId}
                                onChange={(nextFilialDivisionId, filialDivision) => {
                                    setValue('filialDivisionId', nextFilialDivisionId)
                                    setValue('filialId',
                                        filialDivision != null &&
                                        Number(filialDivision.filialId) !== Number(filialId)
                                            ? filialDivision.filialId
                                            : filialId)
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center' }}>
                            <p style={{ width: "50%" }}>Документ санитарно-эпидемиологического нормирования (на
                                соответствие, которого проводится исследование (ГОСТ)
                            </p>
                            <RHFGovernment disabled={disabled} />
                        </Grid>
                        <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center' }}>
                            <RHFIndicator disabled={disabled} />
                        </Grid>
                    </Grid>
                    <LoadingButton disabled={disabled} loading={isSubmitting} type="submit" variant="contained"
                        sx={{ mt: 3, mb: 2, alignSelf: 'flex-end' }}>
                        {isEditing ? 'Сохранить' : 'Добавить'}
                    </LoadingButton>
                </FormProvider>
                {protocols?.length ? <ProtocolDetails action={'details'} title={t("protocolList.detailsText")} reload={reload} open={editProtocolOpen} setOpen={setEditProtocolOpen}
                    protocol={editProtocol} /> : null}
                {pdfOpen && <ResolutionPdf title={"Просмотр PDF"} open={pdfOpen} onClose={() => setPdfOpen(false)} data={res}/>}
            </PageContainer>}
        </DashboardLayout>
    );
}

export default ResolutionCreate;
